import { Location } from "@angular/common";
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  DomSanitizer,
  SafeResourceUrl,
  Title,
} from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { SignInPage } from "components/cognito/sign-in-cognito/sign-in";
import {
  App,
  Events,
  ModalController,
  Nav,
  Platform,
  ToastController,
} from "ionic-angular";
import * as moment from "moment-timezone";
import { FeatureToggleDataService } from "providers/features-service/feature-toggle.data.service";
import { MobileOfflineDataProvider } from "providers/mobile-offline-data/mobile-offline-data";
import { OfflineAsmtDataServiceProvider } from "providers/offline-asmt-data-service/offline-asmt-data-service";
import { SQLiteProgress } from "providers/offline-manager/offline-globals";
import { StateManagementService } from "providers/state-management/state-management.service";
import { Subscription } from "rxjs";
import { CareType } from "../models/patient-chart/care-type";
import { NavigationService } from "../navigation/navigation.service";
import {
  AppStatus,
  ConnectionStatusEnum,
} from "../providers/app-status/app-status";
import { ConfigurationService } from "../providers/configuration/configuration.service";
import { MobileUtilsProvider } from "../providers/mobile-utils/mobile-utils";
import { NavBarServiceProvider } from "../providers/nav-bar-service/nav-bar-service";
import { DatabaseProvider } from "../providers/offline-database/db-connection";
import { UtilityService } from "../providers/utility-service/utility-service";
import { AuthService } from "./auth/auth.service";

@Component({
  selector: "app-root",
  templateUrl: "app.html",
})
export class MyApp implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(Nav) nav: Nav;
  rootPage: any = SignInPage;
  activePage: string;
  assessmentId: null;
  assessmentUrl: SafeResourceUrl;
  carePlanUrl: SafeResourceUrl;
  appSyncStatus: ConnectionStatusEnum = ConnectionStatusEnum.Online;
  connectionStatusEnum = ConnectionStatusEnum;
  isMobilePlatform: boolean;
  syncCount: number;
  warningMsgFlag: boolean;
  sqliteProgressToast: any;
  mobileMode: boolean = false;
  sectionPage: boolean = false;
  visitPage: boolean = false;
  dashboardPage: boolean = false;
  volunteerActivityPage: boolean = false;
  patientCareTypeSubscription: Subscription;
  assessmentTypeSubscription: Subscription;
  sectionIdSubscription: Subscription;
  viewDidEnterSubscription: Subscription;
  curantisHeaderClass: string = "curantisHeader"; // used to update global header color - default color is magenta (AKA 'curantisHeader' css class)
  private subscriptions: Subscription = new Subscription();

  constructor(
    platform: Platform,
    app: App,
    public authSvc: AuthService,
    private titleService: Title,
    private translate: TranslateService,
    public navSvc: NavigationService,
    public stateData: StateManagementService,
    private sanitizer: DomSanitizer,
    public mobileUtil: MobileUtilsProvider,
    public mobileOfflineData: MobileOfflineDataProvider,
    public database: DatabaseProvider,
    public toastCtrl: ToastController,
    public events: Events,
    public appStatus: AppStatus,
    public modalCtrl: ModalController,
    public OfflineAsmtDataService: OfflineAsmtDataServiceProvider,
    public mobileUtilsProvider: MobileUtilsProvider,
    private configurationService: ConfigurationService,
    public utilService: UtilityService,
    private featureToggleService: FeatureToggleDataService,
    private location: Location,
    private navBarService: NavBarServiceProvider
  ) {
    // CO-3110: ion-content measures its header/footer once, and when a view
    // is presented while another overlay is still tearing down that
    // measurement can land wrong, leaving the footer overlapping the form
    // (Add Order, Edit Cert, Volunteer Activity, and any other footered
    // modal). Re-measure every view — pages and modals alike — once it has
    // fully entered; Content.resize() is a no-op when the dimensions are
    // already correct. Held in its own field (NOT this.subscriptions):
    // ngAfterViewInit unsubscribes that composite, and in RxJS 5 a closed
    // composite immediately unsubscribes anything added to it, which would
    // silently kill this hook at bootstrap.
    this.viewDidEnterSubscription = app.viewDidEnter.subscribe((view) => {
      const content = view && view.getContent && view.getContent();
      if (content && typeof content.resize === "function") {
        setTimeout(() => content.resize());
      }
    });

    // listen for patient care type changes to update global header color
    this.patientCareTypeSubscription = this.stateData
      .getPatientCareTypeObserver()
      .subscribe((careType: string) => {
        this.setGlobalHeaderStyle();
      });
    // listen for page change to update global header
    this.assessmentTypeSubscription = this.stateData
      .getAssessmentTypeObserver()
      .subscribe((assessmentType: string) => {
        this.setGlobalHeaderStyle();
      });
    // listen for section Id change to update global header
    this.sectionIdSubscription = this.stateData
      .getSectionIdObserver()
      .subscribe((sectionId: string) => {
        this.setGlobalHeaderStyle();
      });

    this.assessmentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `${configurationService.apiUrl}/idp-logout`
    );
    this.carePlanUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `${configurationService.planOfCareUrl}/idp-logout`
    );
    this.isMobilePlatform = this.mobileUtil.isMobilePlatform();
    this.appSyncStatus = this.appStatus.getAppStatus();

    platform.ready().then(this.onPlatformReady.bind(this));

    this.initTranslate();

    this.translate
      .get("curantisClinical")
      .subscribe(this.updateTitle.bind(this));

    this.navSvc
      .initialize()
      .then(() => {
        this.navSvc
          .getNavigationObservable()
          .subscribe(this.onNavigation.bind(this));
      })
      .catch((error) => {
        console.error("Error initializing NavigationService:", error);
      });

    // Re-sync patient context on URL navigation:
    //  - popstate: browser back/forward, history.back/forward()
    //  - hashchange: manual address-bar edits (manual edits fire hashchange,
    //    NOT popstate — per WHATWG/MDN). Without the hashchange listener,
    //    manual URL edits leave patient context stale and the four
    //    sessionStorage keys point at the previous patient. (NOVA-1610)
    //
    // Matches both #/reports/<id>/... and patient-details routes such as
    // #/referral-tabs/<careType>/patient-details/<id>.
    //
    // NOVA-1645 — extend coverage to the non-profile patient sections so a
    // manual URL :id edit (or popstate) re-syncs patient context for them too.
    // These come in two route shapes, both carrying the patient chart id in
    // the hash:
    //   - Family 1: #/patient/<patChartId>/<section>  (carePlan, planOfCare,
    //     assessment, cti, careTeams, docManagement, orderEntryOM, ...).
    //     `/patient/(\d+)/` requires a digit right after /patient/, so it does
    //     NOT collide with the profile route #/.../patient/patient-details/<id>
    //     (next segment there is non-numeric).
    //   - Family 2: #/<section-keyword>/<patientId>  (benefits, patient-
    //     benefits, notes, patient-allergies, patient-medications,
    //     patient-activity). Enumerated explicitly — no catch-all — so we never
    //     mistake an order/claim/report numeric id for a patient id.
    // The duplicated-segment normalization below stays profile-specific; the
    // id-resolution / sessionStorage rewrite / publish logic that follows is
    // route-agnostic once a patient id is matched here.
    const handleUrlChange = () => {
      setTimeout(() => {
        const hash = window.location.hash;
        const urlIdMatch =
          hash.match(/^#\/reports\/(\d+)/) ||
          hash.match(/\/patient-details\/(\d+)/) ||
          hash.match(/\/patient\/(\d+)(?:\/|$)/) ||
          hash.match(
            /\/(?:patient-benefits|benefits|notes|patient-allergies|patient-medications|patient-activity)\/(\d+)/
          );
        if (!urlIdMatch) {
          return;
        }

        // NOVA-1610 — manual URL edits often produce a duplicated
        // `/patient-details/patient-details/<id>` segment because the
        // browser appends rather than replaces. Ionic 3's deep-linker
        // can't resolve the malformed form and later re-serializes the
        // URL from the active view's stored segment data (pre-edit id),
        // which leaves the address bar showing the previous patient.
        // Collapse the duplication via replaceState (does NOT re-fire
        // hashchange / popstate, so no loop). Normalize BEFORE the
        // idempotency check below so press-Enter on a clean-looking URL
        // (which still produces a duplicated hash internally) still
        // cleans up the URL even when no state rewrite is needed.
        const canonicalHash = hash.replace(
          /(\/patient-details)\/patient-details\//g,
          "$1/"
        );
        if (canonicalHash !== hash) {
          window.history.replaceState(null, "", canonicalHash);
        }

        // NOVA-1610 — use parsePatientChartIdsFromUrl's < 199999 heuristic
        // to determine whether the URL id is internal or external. The
        // previous always-treat-urlId-as-external logic broke legacy
        // patients: e.g. Chelsey McKenzie has internal 203560 / external
        // 90928. With URL = /patient-details/203560 (correct per
        // convention), the prior code labeled 203560 as externalId:true,
        // corrupted sessionStorage["externalId"] from 90928 → 203560, and
        // caused brief-bar to issue
        // /patient-charts/203560/brief?useExternalId=true → 404.
        const parsed =
          this.navBarService.parsePatientChartIdsFromUrl(canonicalHash);
        const urlId = parsed && parsed.patientChartId;
        const urlIsExternal = parsed && parsed.externalId;
        if (!urlId) {
          return;
        }

        const navBarInternalId = this.navBarService.getPatientChartId();
        const navBarExternalId = this.navBarService.getExternalId();

        // Resolve both ids. If navBarService caches the OTHER id for the
        // same patient, use it. Otherwise fall back to urlId for the
        // unknown side — subscribers' fetch will resolve the patient and
        // referrals-service.getPatientById's .map will then rewrite the
        // 4 sessionStorage keys with the correct values.
        let externalId: number;
        let internalId: number;
        if (urlIsExternal) {
          const isSamePatient =
            navBarExternalId && Number(navBarExternalId) === urlId;
          externalId = urlId;
          internalId =
            isSamePatient && navBarInternalId
              ? Number(navBarInternalId)
              : urlId;
        } else {
          const isSamePatient =
            navBarInternalId && Number(navBarInternalId) === urlId;
          externalId =
            isSamePatient && navBarExternalId
              ? Number(navBarExternalId)
              : urlId;
          internalId = urlId;
        }

        const patientChartInfo = {
          id: externalId,
          externalId: urlIsExternal,
          patientChartId: internalId,
        };

        // Skip rewrite + publish if sessionStorage already reflects this
        // patient — back+hashchange firing in close succession both run
        // this handler, and press-Enter on a no-change URL also lands
        // here. We don't want to clobber sessionStorage with placeholder
        // values when the existing keys are already correct.
        const existing = window.sessionStorage.getItem("patientChartInfo");
        if (existing) {
          try {
            const parsedExisting = JSON.parse(existing);
            if (
              parsedExisting &&
              Number(parsedExisting.patientChartId) ===
                Number(patientChartInfo.patientChartId) &&
              Number(parsedExisting.id) === Number(patientChartInfo.id)
            ) {
              return;
            }
          } catch (e) {
            // fall through; rewrite below
          }
        }

        window.sessionStorage.setItem(
          "patientChartInfo",
          JSON.stringify(patientChartInfo)
        );
        window.sessionStorage.setItem("externalId", String(externalId));
        window.sessionStorage.setItem("patientChartId", String(internalId));
        window.sessionStorage.setItem("patChartId", String(internalId));
        this.events.publish("patientChartIdChange", patientChartInfo);
      });
    };
    window.addEventListener("popstate", handleUrlChange);
    window.addEventListener("hashchange", handleUrlChange);

    //check if it is mobile mode
    if (mobileUtilsProvider.checkIfMobile()) {
      this.mobileMode = true;
    }
    // Creating Menu for Mobile Mode in assessment screen
    events.subscribe("user:InSectionPage", () => {
      this.sectionPage = true;
      this.visitPage = false;
      this.dashboardPage = false;
      this.volunteerActivityPage = false;
    });

    // Creating Menu for Mobile Mode in assessment visit screen
    events.subscribe("user:InVisitPage", () => {
      this.sectionPage = false;
      this.visitPage = true;
      this.dashboardPage = false;
      this.volunteerActivityPage = false;
    });

    // Creating Menu for Mobile Mode in Volunteer Activity screen
    events.subscribe("user:InVolunteerActivityPage", () => {
      this.sectionPage = false;
      this.visitPage = false;
      this.dashboardPage = false;
      this.volunteerActivityPage = true;
    });

    // Creating Menu for Mobile Mode in dashboard screen
    events.subscribe("user:InDashboardPage", () => {
      this.subscriptions.unsubscribe();
      this.subscriptions.add(
        this.authSvc.UserInfoChange.subscribe((value) => {
          this.featureToggleService.loadToggles().subscribe({
            next: () => {
              this.stateData.setupForFeatureList();
              setTimeout(() => {
                this.events.publish("user:ReloadToggles");
              }, 0);
            },
            error: (err) => {
              console.error("Error loading feature toggles", err);
            },
          });
        })
      );
      if (mobileUtilsProvider.checkIfMobile()) {
        this.sectionPage = false;
        this.visitPage = false;
        this.dashboardPage = true;
        this.volunteerActivityPage = false;
      }
    });
  }

  private onPlatformReady() {
    // Okay, so the platform is ready and our plugins are available.
    // Here you can do any higher level native things you might need.
    this.initTranslate();
    this.subscribeNetworkEvent();
  }

  ngOnInit() {
    this.translate.get("curantisClinical").subscribe((result) => {
      this.titleService.setTitle(result);
    });

    this.showSQLiteProgress = this.showSQLiteProgress.bind(this);
  }

  subscribeNetworkEvent() {
    if (this.isMobilePlatform) {
      this.appStatus.initializeNetworkEvents();
      this.events.subscribe("networkchange", (eventData) => {
        this.appSyncStatus = eventData.status;
        if (this.appSyncStatus === ConnectionStatusEnum.Online) {
          this.syncOfflineData();
        } else if (this.appSyncStatus === ConnectionStatusEnum.Offline) {
          // this.navSvc.goToInternalPage('dashboard-page', {});
        }
      });

      // Fetch offline changes and show SYNC popup when SQLite is ready
      this.events.subscribe("sqlite-ready", (eventData) => {
        if (!this.appStatus.isOffline()) {
          this.syncOfflineData();
        }
      });

      this.events.subscribe("onSQLiteProgress", this.showSQLiteProgress);
    } else {
      // Browser network event
      window.addEventListener(
        "online",
        (e) => {
          this.appSyncStatus = ConnectionStatusEnum.Online;
        },
        false
      );

      window.addEventListener(
        "offline",
        (e) => {
          this.appSyncStatus = ConnectionStatusEnum.Offline;
        },
        false
      );
    }
  }

  showSQLiteProgress(status) {
    if (status === SQLiteProgress.Started) {
      this.dismissSQLiteProgressToast();
      this.sqliteProgressToast = this.toastCtrl.create({
        message: "Loading today’s patient assessment data for offline use....",
        duration: 120 * 1000, //Default wait time, In case of error it will close after 2 min
        cssClass: "success",
      });
      this.sqliteProgressToast.present();
    } else {
      this.dismissSQLiteProgressToast();
    }
  }

  dismissSQLiteProgressToast() {
    if (this.sqliteProgressToast) {
      this.sqliteProgressToast.dismiss();
      this.sqliteProgressToast = null;
    }
  }

  toggleSyncWarningMsg() {
    this.warningMsgFlag = !this.warningMsgFlag;
  }

  syncOfflineData() {
    this.mobileOfflineData.getOfflineActions().subscribe((offlineActions) => {
      this.syncCount = 0;
      if (offlineActions.length > 0) {
        offlineActions.forEach((data) => {
          data.expire_at = moment(data.expire_at).format("MM/DD/YYYY");
          data.isSync = false;
        });
        let manualSyncModal = this.modalCtrl.create(
          "ManualSyncModalPage",
          {
            modalData: offlineActions,
          },
          { enableBackdropDismiss: false, cssClass: "manual-sync-popover" }
        );
        manualSyncModal.onDidDismiss(() => {
          this.warningMsgFlag = false;
          this.mobileOfflineData
            .getOfflineActionsCount()
            .subscribe((totalActions) => {
              this.syncCount = totalActions;
            });
        });
        manualSyncModal.present();
      }
    });
  }

  private initTranslate() {
    // Set the default language for translation strings, and the current language.
    this.translate.addLangs(["en", "es"]);
    this.translate.setDefaultLang("en");

    const browserLang = this.translate.getBrowserLang();
    this.translate.use(browserLang.match(/en|es/) ? browserLang : "en");
  }

  private updateTitle(newTitle) {
    this.titleService.setTitle(newTitle);
  }

  private onNavigation(data) {
    if (data) {
      if (data.page === "sign-in-page") {
        this.nav.setRoot(SignInPage, data.params);
        this.rootPage = SignInPage;
        this.location.replaceState("/");
      } else {
        // this.rootPage=data.page;
        this.nav.setRoot(data.page, data.params);
      }
    }
  }

  openPage(id: string) {
    // Reset the content nav to have just this page
    // we wouldn't want the back button to show in this scenario
    var pageassessmentId =
      this.nav.getActive().data.assessmentId || this.assessmentId;
    let chartId = this.stateData.getChartId();
    this.nav.setRoot("SectionPage", {
      patChartId: chartId,
      assessmentId: pageassessmentId,
      sectionId: id,
    });
    this.activePage = id;
  }

  ngAfterViewInit() {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }

    if (this.authSvc.isLoggedIn()) {
      this.subscriptions.add(
        this.authSvc.UserInfoChange.subscribe((value) => {
          this.featureToggleService.loadToggles().subscribe({
            next: () => {
              this.stateData.setupForFeatureList();
            },
            error: (err) => {
              console.error("Error loading feature toggles", err);
            },
          });
        })
      );
    }
  }

  setGlobalHeaderStyle() {
    if (
      this.configurationService.getConfig().isLocal ||
      this.configurationService.getConfig().isDev ||
      this.configurationService.getConfig().isQA
    ) {
      if (
        this.stateData.patientCareType &&
        this.stateData.patientCareType.toLowerCase() ===
          CareType[CareType.Palliative].toLowerCase() &&
        (this.utilService.isDynamicGlobalHeader(
          this.stateData.getAssessmentType(),
          "assessmentType"
        ) ||
          this.utilService.isDynamicGlobalHeader(
            this.stateData.getCurrentPage(),
            "currentPage"
          ) ||
          this.utilService.isDynamicGlobalHeader(
            this.stateData.getSectionId(),
            "sectionId"
          ))
      ) {
        // set global header to darkskyblue (i.e. palliative) background color
        this.curantisHeaderClass = "curantisHeader-palliative"; // defined in 'app.scss'
      } else {
        // set global header to default magenta background color
        this.curantisHeaderClass = "curantisHeader"; // defined in 'app.scss'
      }
    } else {
      this.curantisHeaderClass = "curantisHeader"; // defined in 'app.scss'
    }
  }

  ngOnDestroy() {
    if (this.assessmentTypeSubscription) {
      this.assessmentTypeSubscription.unsubscribe();
    }
    if (this.patientCareTypeSubscription) {
      this.patientCareTypeSubscription.unsubscribe();
    }
    if (this.sectionIdSubscription) {
      this.sectionIdSubscription.unsubscribe();
    }
    if (this.viewDidEnterSubscription) {
      this.viewDidEnterSubscription.unsubscribe();
    }
    this.subscriptions.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts